/**
 * HERE Maps Geocoding Service
 * @description Service for interacting with HERE Maps Geocoding APIs
 * @see https://developer.here.com/documentation/geocoding-search-api
 */

const HERE_API_KEY = import.meta.env.VITE_HERE_API_KEY;
const HERE_GEOCODE_BASE_URL = 'https://geocode.search.hereapi.com/v1';
const HERE_AUTOSUGGEST_BASE_URL = 'https://autosuggest.search.hereapi.com/v1';

/**
 * ISO 3166-1 alpha-3 to alpha-2 country code mapping
 * HERE API returns alpha-3 codes, but we need alpha-2 for our system
 */
const COUNTRY_CODE_ALPHA3_TO_ALPHA2: Record<string, string> = {
  AFG: 'AF',
  ALB: 'AL',
  DZA: 'DZ',
  AND: 'AD',
  AGO: 'AO',
  ATG: 'AG',
  ARG: 'AR',
  ARM: 'AM',
  AUS: 'AU',
  AUT: 'AT',
  AZE: 'AZ',
  BHS: 'BS',
  BHR: 'BH',
  BGD: 'BD',
  BRB: 'BB',
  BLR: 'BY',
  BEL: 'BE',
  BLZ: 'BZ',
  BEN: 'BJ',
  BTN: 'BT',
  BOL: 'BO',
  BIH: 'BA',
  BWA: 'BW',
  BRA: 'BR',
  BRN: 'BN',
  BGR: 'BG',
  BFA: 'BF',
  BDI: 'BI',
  KHM: 'KH',
  CMR: 'CM',
  CAN: 'CA',
  CPV: 'CV',
  CAF: 'CF',
  TCD: 'TD',
  CHL: 'CL',
  CHN: 'CN',
  COL: 'CO',
  COM: 'KM',
  COG: 'CG',
  COD: 'CD',
  CRI: 'CR',
  CIV: 'CI',
  HRV: 'HR',
  CUB: 'CU',
  CYP: 'CY',
  CZE: 'CZ',
  DNK: 'DK',
  DJI: 'DJ',
  DMA: 'DM',
  DOM: 'DO',
  ECU: 'EC',
  EGY: 'EG',
  SLV: 'SV',
  GNQ: 'GQ',
  ERI: 'ER',
  EST: 'EE',
  SWZ: 'SZ',
  ETH: 'ET',
  FJI: 'FJ',
  FIN: 'FI',
  FRA: 'FR',
  GAB: 'GA',
  GMB: 'GM',
  GEO: 'GE',
  DEU: 'DE',
  GHA: 'GH',
  GRC: 'GR',
  GRD: 'GD',
  GTM: 'GT',
  GIN: 'GN',
  GNB: 'GW',
  GUY: 'GY',
  HTI: 'HT',
  HND: 'HN',
  HUN: 'HU',
  ISL: 'IS',
  IND: 'IN',
  IDN: 'ID',
  IRN: 'IR',
  IRQ: 'IQ',
  IRL: 'IE',
  ISR: 'IL',
  ITA: 'IT',
  JAM: 'JM',
  JPN: 'JP',
  JOR: 'JO',
  KAZ: 'KZ',
  KEN: 'KE',
  KIR: 'KI',
  PRK: 'KP',
  KOR: 'KR',
  KWT: 'KW',
  KGZ: 'KG',
  LAO: 'LA',
  LVA: 'LV',
  LBN: 'LB',
  LSO: 'LS',
  LBR: 'LR',
  LBY: 'LY',
  LIE: 'LI',
  LTU: 'LT',
  LUX: 'LU',
  MDG: 'MG',
  MWI: 'MW',
  MYS: 'MY',
  MDV: 'MV',
  MLI: 'ML',
  MLT: 'MT',
  MHL: 'MH',
  MRT: 'MR',
  MUS: 'MU',
  MEX: 'MX',
  FSM: 'FM',
  MDA: 'MD',
  MCO: 'MC',
  MNG: 'MN',
  MNE: 'ME',
  MAR: 'MA',
  MOZ: 'MZ',
  MMR: 'MM',
  NAM: 'NA',
  NRU: 'NR',
  NPL: 'NP',
  NLD: 'NL',
  NZL: 'NZ',
  NIC: 'NI',
  NER: 'NE',
  NGA: 'NG',
  MKD: 'MK',
  NOR: 'NO',
  OMN: 'OM',
  PAK: 'PK',
  PLW: 'PW',
  PAN: 'PA',
  PNG: 'PG',
  PRY: 'PY',
  PER: 'PE',
  PHL: 'PH',
  POL: 'PL',
  PRT: 'PT',
  QAT: 'QA',
  ROU: 'RO',
  RUS: 'RU',
  RWA: 'RW',
  KNA: 'KN',
  LCA: 'LC',
  VCT: 'VC',
  WSM: 'WS',
  SMR: 'SM',
  STP: 'ST',
  SAU: 'SA',
  SEN: 'SN',
  SRB: 'RS',
  SYC: 'SC',
  SLE: 'SL',
  SGP: 'SG',
  SVK: 'SK',
  SVN: 'SI',
  SLB: 'SB',
  SOM: 'SO',
  ZAF: 'ZA',
  SSD: 'SS',
  ESP: 'ES',
  LKA: 'LK',
  SDN: 'SD',
  SUR: 'SR',
  SWE: 'SE',
  CHE: 'CH',
  SYR: 'SY',
  TWN: 'TW',
  TJK: 'TJ',
  TZA: 'TZ',
  THA: 'TH',
  TLS: 'TL',
  TGO: 'TG',
  TON: 'TO',
  TTO: 'TT',
  TUN: 'TN',
  TUR: 'TR',
  TKM: 'TM',
  TUV: 'TV',
  UGA: 'UG',
  UKR: 'UA',
  ARE: 'AE',
  GBR: 'GB',
  USA: 'US',
  URY: 'UY',
  UZB: 'UZ',
  VUT: 'VU',
  VAT: 'VA',
  VEN: 'VE',
  VNM: 'VN',
  YEM: 'YE',
  ZMB: 'ZM',
  ZWE: 'ZW',
  // Territories and special regions
  PRI: 'PR',
  GUM: 'GU',
  VIR: 'VI',
  ASM: 'AS',
  HKG: 'HK',
  MAC: 'MO',
};

/**
 * Convert ISO 3166-1 alpha-3 country code to alpha-2
 * @param alpha3 - Three-letter country code (e.g., 'USA', 'DEU')
 * @returns Two-letter country code (e.g., 'US', 'DE') or the original if not found
 */
const convertAlpha3ToAlpha2 = (alpha3: string | null | undefined): string | null => {
  if (!alpha3) return null;
  return COUNTRY_CODE_ALPHA3_TO_ALPHA2[alpha3.toUpperCase()] || alpha3;
};

/**
 * Address component from HERE API response
 */
export type HereAddressComponent = {
  label: string;
  countryCode: string;
  countryName: string;
  stateCode?: string;
  state?: string;
  county?: string;
  city?: string;
  district?: string;
  postalCode?: string;
};

/**
 * Location result from HERE API
 */
export type HereLocationResult = {
  title: string;
  id: string;
  resultType: string;
  localityType?: string;
  address: HereAddressComponent;
  position?: {
    lat: number;
    lng: number;
  };
  distance?: number;
};

/**
 * HERE API Response
 */
type HereGeocodingResponse = {
  items: HereLocationResult[];
};

/**
 * Parsed location data for form auto-population
 */
export type ParsedLocationData = {
  city: string | null;
  state: string | null;
  stateCode: string | null;
  country: string | null;
  countryCode: string | null;
  postalCode: string | null;
};

/**
 * Lookup location by postal code
 * @param postalCode - The postal code to lookup
 * @param countryCode - Optional ISO 3166-1 alpha-3 country code (e.g., 'USA', 'DEU')
 * @returns Promise with location data
 */
export const lookupByPostalCode = async (
  postalCode: string,
  countryCode?: string
): Promise<ParsedLocationData | null> => {
  if (!HERE_API_KEY) {
    console.error('HERE API key is not configured');
    return null;
  }

  if (!postalCode || postalCode.trim().length < 3) {
    return null;
  }

  try {
    const params = new URLSearchParams({
      apiKey: HERE_API_KEY,
      q: postalCode,
      types: 'postalCode',
      limit: '1',
      lang: 'en', // Request English responses for consistent country names
    });

    if (countryCode) {
      params.append('in', `countryCode:${countryCode}`);
    }

    const response = await fetch(`${HERE_GEOCODE_BASE_URL}/geocode?${params.toString()}`);

    if (!response.ok) {
      console.error('HERE API error:', response.status, response.statusText);
      return null;
    }

    const data: HereGeocodingResponse = await response.json();

    if (data.items && data.items.length > 0) {
      const item = data.items[0];
      return {
        city: item.address.city || item.address.district || null,
        state: item.address.state || null,
        stateCode: item.address.stateCode || null,
        country: item.address.countryName || null,
        countryCode: convertAlpha3ToAlpha2(item.address.countryCode),
        postalCode: item.address.postalCode || postalCode,
      };
    }

    return null;
  } catch (error) {
    console.error('HERE geocoding error:', error);
    return null;
  }
};

/**
 * Autosuggest locations based on query
 * @param query - Search query (address, city, postal code, etc.)
 * @param options - Optional configuration
 * @returns Promise with array of location suggestions
 */
export const autosuggestLocations = async (
  query: string,
  options?: {
    limit?: number;
    countryCode?: string;
    lat?: number;
    lng?: number;
  }
): Promise<HereLocationResult[]> => {
  if (!HERE_API_KEY) {
    console.error('HERE API key is not configured');
    return [];
  }

  if (!query || query.trim().length < 2) {
    return [];
  }

  try {
    const params = new URLSearchParams({
      apiKey: HERE_API_KEY,
      q: query,
      limit: String(options?.limit || 5),
      types: 'administrativeArea,postalCode,locality',
    });

    if (options?.countryCode) {
      params.append('in', `countryCode:${options.countryCode}`);
    }

    if (options?.lat && options?.lng) {
      params.append('at', `${options.lat},${options.lng}`);
    }

    const response = await fetch(`${HERE_AUTOSUGGEST_BASE_URL}/autosuggest?${params.toString()}`);

    if (!response.ok) {
      console.error('HERE API error:', response.status, response.statusText);
      return [];
    }

    const data: HereGeocodingResponse = await response.json();
    return data.items || [];
  } catch (error) {
    console.error('HERE autosuggest error:', error);
    return [];
  }
};

/**
 * Get country dial code from country code
 * @param countryCode - ISO 3166-1 alpha-2 country code (e.g., 'US', 'DE')
 * @returns Phone dial code (e.g., '+1', '+49')
 */
export const getDialCodeFromCountry = (countryCode: string): string => {
  const dialCodes: Record<string, string> = {
    US: '+1',
    CA: '+1',
    DE: '+49',
    GB: '+44',
    FR: '+33',
    IT: '+39',
    ES: '+34',
    NL: '+31',
    BE: '+32',
    AT: '+43',
    CH: '+41',
    AU: '+61',
    NZ: '+64',
    JP: '+81',
    CN: '+86',
    KR: '+82',
    AE: '+971',
    SA: '+966',
    MX: '+52',
    BR: '+55',
    NO: '+47',
    SE: '+46',
    DK: '+45',
    FI: '+358',
    IE: '+353',
    PT: '+351',
    PL: '+48',
    CZ: '+420',
    HU: '+36',
    RO: '+40',
    GR: '+30',
    TR: '+90',
    IL: '+972',
    IN: '+91',
    SG: '+65',
    HK: '+852',
    TW: '+886',
    TH: '+66',
    MY: '+60',
    PH: '+63',
    ID: '+62',
    VN: '+84',
    ZA: '+27',
    EG: '+20',
    NG: '+234',
    KE: '+254',
    AR: '+54',
    CL: '+56',
    CO: '+57',
    PE: '+51',
    VE: '+58',
  };

  return dialCodes[countryCode?.toUpperCase()] || '+1';
};
